/*  
This component provides a login, signup page when user sign's up they are taken to otp page where they have to enter a 6 digit otp which 
they recieve in the mail which was entered during the signup. This component is rendered first when any user hits the frontend url.

This component has login page (which has fields like email and password), signup page (which has fields like first name, last name, email,
password, confirm password) and otp page (which has a field of otp number).
*/

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import crypto from 'crypto-js';
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "./LoginModel.css";
import pairpoint from "../../assets/pairpoint.png";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const LoginModel = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isOtpPage, setIsOtpPage] = useState(false);
  const [otp, setOtp] = useState("");
  
  // Signup State
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  // Login State
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Password visibility states
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const validEmailDomains = ["@pairpoint.io", "@gmail.com", "@outlook.com"];
  
  const generateSalt = () => {
    return crypto.lib.WordArray.random(16).toString();
  };
  const hashPassword = (password, salt) => {
    return crypto.PBKDF2(password, salt, {
      keySize: 256/32,
      iterations: 1000
    }).toString();
  };
  const isValidEmailDomain = (email) => {
    const domain = email.substring(email.lastIndexOf("@"));
    return validEmailDomains.includes(domain);
  };
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter both email and password");
      return;
    }
    if (!isValidEmailDomain(email)) {
      toast.error("Invalid email domain. Use @pairpoint.io, @gmail.com, or @outlook.com");
      return;
    }
    try {
      const saltResponse = await axios.get(`${API_BASE_URL}/lgn/get-salt/${email}`);
      const userSalt = saltResponse.data.salt;
      const hashedPassword = hashPassword(password, userSalt);
      const response = await axios.post(`${API_BASE_URL}/lgn/login`, {
        email: email,
        password: hashedPassword
      });
      if (response.status === 200) {
        const userFirstName = response.data.firstname;
        sessionStorage.setItem("userFirstName", userFirstName);
        toast.success("Login successful!");
        setTimeout(() => {
          navigate("/svc-inventory");
        }, 500);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Login failed. Please try again.");
    }
  };
  const handleSignup = async (e) => {
    e.preventDefault();
    if (!isValidEmailDomain(signupEmail)) {
      toast.error("Invalid email domain. Use @pairpoint.io, @gmail.com, or @outlook.com");
      return;
    }
    const passwordPattern = /^(?!@)(?=.*\d)[A-Za-z0-9@]{8,}$/;
    if (!passwordPattern.test(signupPassword)) {
      setPasswordError("Password must be at least 8 characters long, alphanumeric, and should not start with '@','.' and 'numbers'");
      console.log(passwordError);
      return;
    }
    if (signupPassword !== confirmPassword) {
      toast.error("Passwords do not match!");
      return;
    }
    try {
      const salt = generateSalt();
      const hashedPassword = hashPassword(signupPassword, salt);
      const response = await axios.post(`${API_BASE_URL}/lgn/signup`, {
        firstname,
        lastname,
        email: signupEmail,
        password: hashedPassword,
        salt: salt 
      });
      if (response.status === 201) {
        toast.success("Signup successful! Please verify your OTP.");
        setIsOtpPage(true); // Navigate to OTP page
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Signup failed. Please try again.");
    }
  };
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_BASE_URL}/lgn/verify-email-otp`, {
        email: signupEmail,
        otp
      });
      if (response.status === 200) {
        toast.success("OTP Verified! You can now login.");
        setIsLogin(true);
        setIsOtpPage(false);
      } else {
        toast.error("Invalid OTP. Please try again.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "OTP verification failed.");
    }
  };

  return (
    <div className="auth-container">
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />
      <img src={pairpoint} alt="pairpoint logo and name"></img>
      <div className="auth-header">
        {!isOtpPage && (
          <>
            <button
              className={`auth-header-button ${isLogin ? "active" : ""}`}
              onClick={() => { setIsLogin(true); setIsOtpPage(false); }}
            >
              LOG IN
            </button>
            <button
              className={`auth-header-button ${!isLogin ? "active" : ""}`}
              onClick={() => { setIsLogin(false); setIsOtpPage(false); }}
            >
              SIGN UP
            </button>
          </>
        )}
      </div>
      {isOtpPage ? (
        <form onSubmit={handleVerifyOtp}>
          <div className="form-group">
          <p>6 digit OTP is sent to <b>{signupEmail}</b></p>
            <label>Enter OTP:</label>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="auth-button">
            Verify OTP
          </button>
        </form>
      ) : isLogin ? (
        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label>Email:</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Password:</label>
            <div style={{ position: 'relative' }}>
              <input 
                type={showLoginPassword ? "text" : "password"} 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                required 
              />
              <button
                type="button"
                onClick={() => setShowLoginPassword(!showLoginPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                {showLoginPassword ? 
                  <FaEyeSlash size={20} color="#666" /> : 
                  <FaEye size={20} color="#666" />
                }
              </button>
            </div>
          </div>
          <button type="submit" className="auth-button">
            Log In
          </button>
        </form>
      ) : (
        <form onSubmit={handleSignup}>
          <div className="form-group">
            <label>First Name:</label>
            <input type="text" value={firstname} onChange={(e) => setFirstname(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Last Name:</label>
            <input type="text" value={lastname} onChange={(e) => setLastname(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Email ID:</label>
            <input type="email" value={signupEmail} onChange={(e) => setSignupEmail(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Password:</label>
            <div style={{ position: 'relative' }}>
              <input 
                type={showSignupPassword ? "text" : "password"} 
                value={signupPassword} 
                onChange={(e) => setSignupPassword(e.target.value)} 
                required 
              />
              <button
                type="button"
                onClick={() => setShowSignupPassword(!showSignupPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                {showSignupPassword ? 
                  <FaEyeSlash size={20} color="#666" /> : 
                  <FaEye size={20} color="#666" />
                }
              </button>
            </div>
          </div>
          <div className="form-group">
            <label>Confirm Password:</label>
            <div style={{ position: 'relative' }}>
              <input 
                type={showConfirmPassword ? "text" : "password"} 
                value={confirmPassword} 
                onChange={(e) => setConfirmPassword(e.target.value)} 
                required 
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                {showConfirmPassword ? 
                  <FaEyeSlash size={20} color="#666" /> : 
                  <FaEye size={20} color="#666" />
                }
              </button>
            </div>
          </div>
          <button type="submit" className="auth-button">
            Sign Up
          </button>
        </form>
      )}
    </div>
  );
};
export default LoginModel;
 
